import { useEffect, useState } from "preact/hooks"
import { CheckCircle2 } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { DecimalInput } from "../../../components/ui/decimal_input"
import { Label } from "../../../components/ui/label"
import { Checkbox } from "../../../components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { SearchableSelect, type SearchableSelectOption } from "../../../components/searchable_select"
import { getProductDisplayLabel, product_upc, searchProducts, type ProductSearchResult } from "../../../api/product_catalog"
import type { TimePeriod } from "../types"
import type { CatalogSkuSelection } from "../utils/hierarchy_add_row"
import { exact_catalog_match } from "../utils/catalog_resolve"

export interface AddSkuDialogTarget {
  row_id: string
  /** e.g. "WF - Northeast → Sparkling Water - MP" — where the SKU will land. */
  path_caption: string
  /** The product line row's name: the picker offers that line's products unless unticked. */
  product_line?: string
}

interface AddSkuDialogProps {
  target: AddSkuDialogTarget | null
  periods: TimePeriod[]
  default_start_month: string
  on_close: () => void
  on_add: (selection: CatalogSkuSelection, initial_doors: number, initial_cases_per_month: number, start_month: string) => void
}

/**
 * Structured quick-add for one SKU under a product-line row: pick from the catalog
 * (identity + cost come along) instead of renaming a blank "New SKU" row by hand.
 */
export function AddSkuDialog(props: AddSkuDialogProps) {
  const { target, periods, default_start_month, on_close, on_add } = props
  const [query, setQuery] = useState("")
  const [hits, setHits] = useState<ProductSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selection, setSelection] = useState<CatalogSkuSelection | null>(null)
  const [free_text_name, setFreeTextName] = useState("")
  /** Catalog product a free-typed name resolved to (via names, UPCs, or mapping aliases). */
  const [resolved_free_text, setResolvedFreeText] = useState<CatalogSkuSelection | null>(null)
  const [doors, setDoors] = useState(0)
  const [cases, setCases] = useState(0)
  const [cost, setCost] = useState(0)
  const [start_month, setStartMonth] = useState(default_start_month)
  const [only_line, setOnlyLine] = useState(true)
  const line_filter = only_line ? target?.product_line?.trim() ?? "" : ""

  useEffect(() => {
    if (!target) return
    setSelection(null)
    setFreeTextName("")
    setResolvedFreeText(null)
    setDoors(0)
    setCases(0)
    setCost(0)
    setStartMonth(default_start_month)
    setOnlyLine(true)
  }, [target, default_start_month])

  // A typed name that exactly matches a catalog product (or a Forecast Mappings alias)
  // silently attaches that identity instead of warning that actuals can never land.
  useEffect(() => {
    const query = free_text_name.trim()
    setResolvedFreeText(null)
    if (!query || selection) return
    const timer = setTimeout(() => {
      void (async () => {
        const res = await searchProducts(query, 10)
        if (!res.ok) return
        const match = exact_catalog_match(query, res.value.data)
        if (!match) return
        const list_price = Number(match.list_price || 0)
        setResolvedFreeText({
          sku_name: match.name,
          product_sku: match.sku || undefined,
          product_id: match.id,
          product_upc: product_upc(match),
          units_per_case: match.units_per_case,
          cost_per_case: list_price > 0 ? Math.round(list_price * 100) / 100 : 0,
        })
        if (list_price > 0) {
          setCost((prev: number) => (prev > 0 ? prev : Math.round(list_price * 100) / 100))
        }
      })()
    }, 450)
    return () => clearTimeout(timer)
  }, [free_text_name, selection])

  useEffect(() => {
    const timer = setTimeout(() => {
      void (async () => {
        setSearching(true)
        const res = await searchProducts(query.trim(), 25, { product_line: line_filter })
        setHits(res.ok ? res.value.data : [])
        setSearching(false)
      })()
    }, 250)
    return () => clearTimeout(timer)
  }, [query, line_filter])

  const options: SearchableSelectOption[] = hits.map((p: ProductSearchResult) => ({
    value: p.id,
    label: getProductDisplayLabel(p),
  }))

  function handleSelect(productId: string): void {
    const selected = hits.find((p: ProductSearchResult) => p.id === productId)
    if (!selected) return
    const list_price = Number(selected.list_price || 0)
    setSelection({
      sku_name: selected.name,
      product_sku: selected.sku || undefined,
      product_id: selected.id,
      product_upc: product_upc(selected),
      units_per_case: selected.units_per_case,
      cost_per_case: list_price > 0 ? Math.round(list_price * 100) / 100 : 0,
    })
    setFreeTextName("")
    if (list_price > 0) {
      setCost(Math.round(list_price * 100) / 100)
    }
  }

  const effective_name = selection?.sku_name || free_text_name.trim()
  const can_add = Boolean(target && effective_name && cost > 0 && doors > 0 && cases > 0 && start_month)

  function handleAdd(): void {
    if (!can_add) return
    const final: CatalogSkuSelection = selection
      ? { ...selection, cost_per_case: cost }
      : resolved_free_text
        ? { ...resolved_free_text, cost_per_case: cost }
        : { sku_name: free_text_name.trim(), cost_per_case: cost }
    on_add(final, doors, cases, start_month)
  }

  return (
    <Dialog open={target != null} onOpenChange={(next: boolean) => { if (!next) on_close() }}>
      <DialogContent className="max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>Add SKU</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {target ? (
            <p className="text-xs text-gray-500">
              Adding under <span className="font-medium text-gray-800">{target.path_caption}</span>
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label>Catalog SKU</Label>
            {selection ? (
              /* The picked SKU replaces the search box so the state is unmistakable. */
              <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50/70 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{selection.sku_name}</p>
                    <p className="text-[11px] text-gray-600">
                      {selection.product_upc ? `UPC ${selection.product_upc}` : selection.product_sku || "Catalog SKU"}
                      {selection.units_per_case ? ` · ${selection.units_per_case}/case` : ""}
                    </p>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => setSelection(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <SearchableSelect
                options={options}
                value=""
                onValueChange={handleSelect}
                onSearchChange={setQuery}
                placeholder="Search catalog SKU…"
                searchPlaceholder="Type to search SKU or product name…"
                emptyMessage={
                  searching
                    ? "Searching…"
                    : line_filter
                      ? `No products found in ${line_filter} — untick the box below to search the whole catalog`
                      : "No products found"
                }
              />
            )}
            {target?.product_line?.trim() ? (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <Checkbox checked={only_line} onCheckedChange={(checked: boolean) => setOnlyLine(Boolean(checked))} />
                Only products in {target.product_line.trim()}
              </label>
            ) : null}
          </div>
          <div className={selection ? "hidden" : "space-y-1.5"}>
            <Label>Or type a name (not in catalog yet)</Label>
            <Input
              value={free_text_name}
              onInput={(e) => {
                setFreeTextName((e.target as HTMLInputElement).value)
                if ((e.target as HTMLInputElement).value.trim()) setSelection(null)
              }}
              placeholder="e.g. Cherry Golden Lime (6-pack)"
            />
            {free_text_name.trim() && !selection ? (
              resolved_free_text ? (
                <p className="text-[11px] text-emerald-700">
                  Matches {resolved_free_text.sku_name}
                  {resolved_free_text.product_upc ? ` (UPC ${resolved_free_text.product_upc})` : ""} — actuals can
                  land on this row.
                </p>
              ) : (
                <p className="text-[11px] text-amber-700">
                  No catalog or mapping match — this row will not receive QuickBooks actuals until the product
                  exists in the catalog (or is mapped on the Forecast Mappings page).
                </p>
              )
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Initial doors</Label>
              <Input
                type="number"
                min={1}
                value={doors || ""}
                onInput={(e) => setDoors(parseInt((e.target as HTMLInputElement).value) || 0)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cases per month</Label>
              {/* A per-store rate is fractional (1.2 a month); parseInt used to drop the decimals. */}
              <DecimalInput value={cases} onValueChange={(value: number) => setCases(value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Cost per case ($)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={cost || ""}
                onInput={(e) => setCost(parseFloat((e.target as HTMLInputElement).value) || 0)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Start month</Label>
              <Select value={start_month || undefined} onValueChange={(v: string) => setStartMonth(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((p: TimePeriod) => (
                    <SelectItem key={p.period_key} value={p.period_key}>
                      {p.period_key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={on_close}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!can_add}>
              Add SKU
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
